/**
 * Currency option resolution for ``CurrencyInput``.
 *
 * Lives beside the component rather than inside it so the widget file only
 * exports components (react-refresh) and so the option set can be unit tested
 * without rendering a `@base-ui` select popup.
 */

export interface CurrencyOption {
  value: string
  label: string
}

/**
 * Resolve the currency options the dropdown may offer.
 *
 * Per-field options (a MONEY attribute's ``configuration_data.options``) win;
 * otherwise the org's selected currencies
 * (``OrganisationPreferences.available_currencies``) are the whole list. There
 * is no hardcoded fallback - an org that switched on only USD + CAD is never
 * offered GBP / EUR / AUD (TA-19).
 *
 * ``selected`` (the currency already on the record) is appended when absent so
 * historic values still render: showing what is stored is not the same as
 * offering a new choice.
 */
export function resolveCurrencyOptions(
  currencyOptions: CurrencyOption[] | undefined,
  availableCurrencies: readonly string[],
  selected: string,
): CurrencyOption[] {
  const base =
    currencyOptions && currencyOptions.length > 0
      ? currencyOptions
      : availableCurrencies.map((code) => ({ value: code, label: code }))
  if (selected && !base.some((option) => option.value === selected)) {
    return [...base, { value: selected, label: selected }]
  }
  return base
}
